import { useState } from "react";
import useSWR from "swr";
import { Table, Button, Group, Stack, Text, Modal, TextInput, NumberInput, ActionIcon, Alert, Tooltip, SegmentedControl, Badge } from "@mantine/core";
import { Archive, ArchiveRestore, Pencil, Search } from "lucide-react";
import { Contacts } from "../../../shared/components/Contacts";
import { Pagination } from "../../../shared/components/Pagination";
import { StudentFormFields, StudentFormData, toStudentFormData, fromStudentFormData } from "../../../shared/components/StudentFormFields";
import { useFetch, getStudents, updateStudent, setStudentArchived, Student, StudentStatus } from "../../../shared/api";
import { ErrorDisplay, Loading, formatFullName } from "../../../shared/utils";
import { FilterBar, useFilterDraft } from "../../../shared/components/FilterBar";
import { PersonName } from "../../../shared/components/PersonName";

const PAGE_SIZE = 50;

export function UsersTab() {
  const { fetch } = useFetch();
  const [applied, setApplied] = useState<{ search: string; status: StudentStatus }>({ search: "", status: "active" });
  const { draft, patch, dirty } = useFilterDraft(applied);
  const [page, setPage] = useState(1);
  const apply = (f: typeof applied) => { setApplied({ ...f, search: f.search.trim() }); setPage(1); };
  const { search, status } = applied;

  const { data, error, isLoading, mutate } = useSWR(["admin/students", search, status, page], () => getStudents({ search, status, limit: PAGE_SIZE, start: (page - 1) * PAGE_SIZE, fetch }));
  const [archiveTarget, setArchiveTarget] = useState<Student | null>(null);
  const [archiving, setArchiving] = useState<string | null>(null);
  const [archiveError, setArchiveError] = useState<string | null>(null);

  async function toggleArchived(s: Student, archived: boolean) {
    setArchiving(s.phystech_email); setArchiveError(null);
    try { await setStudentArchived({ phystech_email: s.phystech_email, archived, fetch }); setArchiveTarget(null); await mutate(); }
    catch (e: any) { setArchiveError(e?.message ?? String(e)); }
    finally { setArchiving(null); }
  }
  const [editTarget, setEditTarget] = useState<Student | null>(null);
  const [editForm, setEditForm] = useState<StudentFormData | null>(null);
  const [activistId, setActivistId] = useState<number | string>("");
  const [saving, setSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  async function saveEdit() {
    if (!editTarget || !editForm) return;
    setSaving(true); setEditError(null);
    try {
      const newActivistId = activistId === "" ? null : Number(activistId);
      // Sent only when edited: otherwise a new mipt.tech handle clears it for a fresh lookup.
      const patch = { ...fromStudentFormData(editForm), ...(newActivistId !== (editTarget.mipt_tech_activist_id ?? null) && { mipt_tech_activist_id: newActivistId }) };
      await updateStudent({ phystech_email: editTarget.phystech_email, patch, fetch });
      setEditTarget(null); setEditForm(null); await mutate();
    } catch (e: any) { setEditError(e?.message ?? String(e)); }
    finally { setSaving(false); }
  }

  if (error) return <ErrorDisplay error={error} />;

  return (
    <Stack>
      <FilterBar dirty={dirty} onApply={() => apply(draft)} onReset={() => apply({ search: "", status: "active" })}>
        <TextInput style={{ flex: 1 }} placeholder="Поиск по имени или физтех-почте" leftSection={<Search size={16} />} value={draft.search} onChange={e => patch({ search: e.target.value })} />
        <SegmentedControl value={draft.status} onChange={v => patch({ status: v as StudentStatus })} data={[{ value: "active", label: "Активные" }, { value: "archived", label: "Архив" }, { value: "all", label: "Все" }]} />
      </FilterBar>
      {archiveError && !archiveTarget && <Alert color="red" withCloseButton onClose={() => setArchiveError(null)}>{archiveError}</Alert>}
      {isLoading || !data ? <Loading /> : (
        <>
          <Table highlightOnHover>
            <Table.Thead><Table.Tr><Table.Th w={240}>ФИО</Table.Th><Table.Th>Контакты</Table.Th><Table.Th w={90} /></Table.Tr></Table.Thead>
            <Table.Tbody>
              {data.map(s => (
                <Table.Tr key={s.phystech_email}>
                  <Table.Td>
                    <Group gap="xs" wrap="nowrap">
                      <PersonName archived={!!s.archived_at}>{formatFullName(s, true)}</PersonName>
                      {s.archived_at && <Badge size="xs" color="orange" variant="outline">архив</Badge>}
                    </Group>
                  </Table.Td>
                  <Table.Td><Contacts c={s} /></Table.Td>
                  <Table.Td>
                    <Group justify="flex-end" gap={4} wrap="nowrap">
                      <Tooltip label="Редактировать"><ActionIcon variant="subtle" color="gray" onClick={() => { setEditTarget(s); setEditForm(toStudentFormData(s)); setActivistId(s.mipt_tech_activist_id ?? ""); setEditError(null); }}><Pencil size={16} /></ActionIcon></Tooltip>
                      {s.archived_at
                        ? <Tooltip label="Вернуть из архива"><ActionIcon variant="subtle" color="gray" loading={archiving === s.phystech_email} onClick={() => toggleArchived(s, false)}><ArchiveRestore size={16} /></ActionIcon></Tooltip>
                        : <Tooltip label="В архив"><ActionIcon variant="subtle" color="orange" onClick={() => { setArchiveTarget(s); setArchiveError(null); }}><Archive size={16} /></ActionIcon></Tooltip>}
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
              {data.length === 0 && <Table.Tr><Table.Td colSpan={3}><Text c="dimmed" ta="center" py="md">{search ? "Ничего не найдено" : status === "archived" ? "Архив пуст" : "Студентов пока нет"}</Text></Table.Td></Table.Tr>}
            </Table.Tbody>
          </Table>
          <Pagination page={page} shown={data.length} hasNext={data.length >= PAGE_SIZE} onChange={setPage} />
        </>
      )}

      <Modal opened={archiveTarget !== null} onClose={() => setArchiveTarget(null)} title="Архивировать пользователя">
        {archiveTarget && (
          <Stack>
            {archiveError && <Alert color="red">{archiveError}</Alert>}
            <Text fz="sm"><b>{formatFullName(archiveTarget, true)}</b> ({archiveTarget.phystech_email}) больше не сможет войти и пропадёт из списков выбора. Лекции, предметы и часы останутся привязаны к нему. Вернуть можно на вкладке «Архив».</Text>
            <Group justify="flex-end"><Button variant="default" onClick={() => setArchiveTarget(null)}>Отмена</Button><Button variant="light" color="orange" loading={archiving === archiveTarget.phystech_email} onClick={() => toggleArchived(archiveTarget, true)}>В архив</Button></Group>
          </Stack>
        )}
      </Modal>

      <Modal opened={editTarget !== null} onClose={() => setEditTarget(null)} title="Редактировать студента">
        {editForm && editTarget && (
          <Stack>
            {editError && <Alert color="red">{editError}</Alert>}
            <StudentFormFields form={editForm} onChange={u => setEditForm(f => f ? u(f) : null)} disableEmail />
            <NumberInput label="ID активиста mipt.tech" description="Находится по нику mipt.tech при выгрузке ЧРА; вручную — только если поиск не справился" min={1} allowDecimal={false} allowNegative={false} value={activistId} onChange={setActivistId} />
            <Group justify="flex-end"><Button variant="default" onClick={() => setEditTarget(null)}>Отмена</Button><Button loading={saving} disabled={!editForm.last_name.trim() || !editForm.first_name.trim()} onClick={saveEdit}>Сохранить</Button></Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
